import React, { useEffect, useState } from 'react';
import { getToken } from '../../../utils/TokenUtils';
import { API_URL } from '../../../constants/api';
import '../css/SearchTab.css';

export default function SearchTab({ table }) {
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = getToken();

  const operators = [
    'LIKE %...%',
    '=',
    '!=',
    '>',
    '<',
    'IS NULL',
    'IS NOT NULL',
  ];

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/sql/tables/${table}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then((res) => res.json())
      .then((res) => {
        if (res?.columns?.length) {
          setColumns(res.columns);
          setData(res.rows || []);
          setResults(res.rows || []);
        }
      })
      .catch((err) => {
        console.error('Failed to load search columns:', err);
      })
      .finally(() => setLoading(false));
  }, [table, token]);

  const handleFilterChange = (column, field, value) => {
    setFilters((prevFilters) => {
      const existingIndex = prevFilters.findIndex((f) => f.column === column);
      if (existingIndex > -1) {
        const updated = [...prevFilters];
        updated[existingIndex] = { ...updated[existingIndex], [field]: value };
        return updated;
      } else {
        return [
          ...prevFilters,
          {
            column,
            operator: field === 'operator' ? value : 'LIKE %...%',
            value: field === 'value' ? value : '',
          },
        ];
      }
    });
  };

  const handleReset = () => {
    setFilters([]);
    setResults(data);
  };

  const handleSearch = () => {
    const filtered = data.filter((row) =>
      filters.every(({ column, operator, value }) => {
        if (!operator) return true;

        const cell = row[column];
        const cellStr = (cell ?? '').toString().toLowerCase();
        const inputStr = (value ?? '').toLowerCase();

        switch (operator) {
          case '=':
            return cellStr === inputStr;
          case '!=':
            return cellStr !== inputStr;
          case 'LIKE %...%':
            return cellStr.includes(inputStr);
          case '>':
            return Number(cell) > Number(value);
          case '<':
            return Number(cell) < Number(value);
          case 'IS NULL':
            return cell === null || cell === undefined;
          case 'IS NOT NULL':
            return cell !== null && cell !== undefined;
          default:
            return true;
        }
      })
    );

    setResults(filtered);
  };

  if (loading) {
    return <div className="p-3 text-muted">Loading table columns...</div>;
  }

  return (
    <div className="pma-search-wrapper">
      <style>{`
        .pma-search-wrapper {
          font-family: sans-serif;
          font-size: 12px;
        }
        .pma-search-box {
          border: 1px solid #aaa;
          background: #f8f8f8;
          border-radius: 3px;
          padding: 12px;
          margin-bottom: 15px;
        }
        .pma-search-legend {
          font-weight: bold;
          font-size: 13px;
          color: #333;
          margin-bottom: 10px;
        }
        .pma-qbe-table {
          width: 100%;
          border-collapse: collapse;
          border: 1px solid #ccc;
          background: #ffffff;
        }
        .pma-qbe-table th {
          background: linear-gradient(to bottom, #f2f2f2, #e5e5e5);
          border: 1px solid #ccc;
          padding: 5px 8px;
          font-weight: bold;
          color: #333;
        }
        .pma-qbe-table td {
          border: 1px solid #e0e0e0;
          padding: 4px 6px;
        }
        .pma-qbe-input {
          width: 100%;
          font-size: 12px;
          padding: 3px 6px;
          border: 1px solid #aaa;
          border-radius: 2px;
        }
        .pma-qbe-select {
          font-size: 12px;
          padding: 3px 6px;
          border: 1px solid #aaa;
          border-radius: 2px;
          background-color: #fff;
        }
        .pma-search-toolbar {
          display: flex;
          justify-content: flex-end;
          gap: 6px;
          margin-top: 10px;
          padding-top: 8px;
          border-top: 1px solid #e0e0e0;
        }
        .pma-btn {
          background: linear-gradient(to bottom, #f2f2f2, #dcdcdc);
          border: 1px solid #888;
          border-radius: 3px;
          padding: 4px 14px;
          font-size: 12px;
          font-weight: bold;
          color: #222;
          cursor: pointer;
        }
        .pma-btn:hover {
          background: linear-gradient(to bottom, #ffffff, #e0e0e0);
        }
        .pma-btn-primary {
          background: linear-gradient(to bottom, #4d90fe, #4787ed);
          border: 1px solid #3079ed;
          color: #fff;
        }
        .pma-btn-primary:hover {
          background: linear-gradient(to bottom, #357ae8, #2f5bb7);
        }
        .pma-alert-success {
          background-color: #e2f0d9;
          border: 1px solid #b2d8a0;
          color: #2e6818;
          padding: 6px 10px;
          font-size: 12px;
          border-radius: 3px;
          margin-bottom: 12px;
        }
        .pma-results-table {
          width: 100%;
          border-collapse: collapse;
          border: 1px solid #d0d0d0;
          background: #ffffff;
        }
        .pma-results-table th {
          background: linear-gradient(to bottom, #f2f2f2, #e5e5e5);
          border: 1px solid #ccc;
          padding: 5px 8px;
          font-weight: bold;
          color: #333;
          white-space: nowrap;
        }
        .pma-results-table td {
          border: 1px solid #e0e0e0;
          padding: 4px 8px;
          font-family: monospace;
          white-space: nowrap;
        }
        .pma-results-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .pma-results-table tr:hover {
          background-color: #ffffcc;
        }
        .pma-null-tag {
          color: #888;
          font-style: italic;
          background: #eee;
          padding: 1px 4px;
          border-radius: 2px;
        }
      `}</style>

      {/* phpMyAdmin QBE Query Box */}
      <div className="pma-search-box">
        <div className="pma-search-legend">
          Do a "query by example" search on table{' '}
          <span className="font-monospace">`{table}`</span>:
        </div>

        <div className="table-responsive">
          <table className="pma-qbe-table">
            <thead>
              <tr>
                <th style={{ width: '25%' }}>Column</th>
                <th style={{ width: '25%' }}>Operator</th>
                <th style={{ width: '50%' }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {columns.map((col) => {
                const filter = filters.find((f) => f.column === col) || {
                  operator: 'LIKE %...%',
                  value: '',
                };

                const isNullOperator = ['IS NULL', 'IS NOT NULL'].includes(
                  filter.operator
                );

                return (
                  <tr key={col}>
                    <td className="fw-bold text-secondary">{col}</td>
                    <td>
                      <select
                        className="pma-qbe-select"
                        value={filter.operator}
                        onChange={(e) =>
                          handleFilterChange(col, 'operator', e.target.value)
                        }
                      >
                        {operators.map((op) => (
                          <option key={op} value={op}>
                            {op}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="pma-qbe-input"
                        value={filter.value}
                        disabled={isNullOperator}
                        onChange={(e) =>
                          handleFilterChange(col, 'value', e.target.value)
                        }
                        placeholder={isNullOperator ? 'N/A' : 'Enter criteria'}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="pma-search-toolbar">
          <button type="button" className="pma-btn" onClick={handleReset}>
            Reset
          </button>
          <button
            type="button"
            className="pma-btn pma-btn-primary"
            onClick={handleSearch}
          >
            Go
          </button>
        </div>
      </div>

      {/* Query Results Section */}
      <div className="pma-results-section">
        <div className="pma-alert-success">
          Showing {results.length} matching row(s) in table{' '}
          <strong>{table}</strong>.
        </div>

        {results.length > 0 ? (
          <div className="table-responsive">
            <table className="pma-results-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {columns.map((col) => {
                      const val = row[col];
                      return (
                        <td key={col}>
                          {val !== null && val !== undefined ? (
                            String(val)
                          ) : (
                            <span className="pma-null-tag">NULL</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-muted p-2">
            MySQL returned an empty result set for this search filter.
          </div>
        )}
      </div>
    </div>
  );
}
